import React from 'react';
import { css } from '@emotion/react';
import { BridgedAlert as Alert } from 'components/common/feedback/bridged-alert';
import { useLocalStorageState } from 'ahooks';
import styled from 'types/theme.types';
import { FormattedMessage } from 'react-intl';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';

const CURRENT_BANNER_ID = 'project-introspect-early-access';

const BANNER_LINK = 'https://www.envzero.com/early-access-project-introspect';

const AnnouncementBanner: React.FC = () => {
  const [dismissed, setDismissed] = useLocalStorageState<boolean>(
    `announcement-banner-dismissed-${CURRENT_BANNER_ID}`,
    {
      defaultValue: false
    }
  );

  if (dismissed) {
    return null;
  }

  return (
    <StyledAlert
      data-e2e="announcement-banner"
      type="info"
      banner
      showIcon={false}
      closable
      onClose={() => setDismissed(true)}
      message={
        <BannerContent>
          <span>
            <FormattedMessage id="announcement.banner.message" />
          </span>
          <BannerLink data-e2e="announcement-banner-cta" href={BANNER_LINK} target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="announcement.banner.cta" />
          </BannerLink>
        </BannerContent>
      }
    />
  );
};

/**
 * This banner is a promotion, not a status message, so it keeps the brand accent fill on both sides
 * rather than rendering as a generic info Alert. `surface.primary.action` is the accent fill and holds
 * the brand lime in both modes, so `text.onAction` is the matching foreground - see that token's own
 * note that mirroring it to white fails contrast.
 *
 * The three other background overrides in this family are status alerts and were deliberately not
 * carried over: an ad-hoc white "error" or purple "info" is the drift this restyle removes.
 */
const StyledAlert = styled(Alert)`
  ${legacyOnly(
    theme => css`
      &.ant-alert {
        padding: 6px 20px;
        background-color: ${theme.limeYellow};
      }

      .ant-alert-message {
        color: ${theme.secondaryBlack};
        font-size: 14px;
      }

      .ant-alert-close-icon {
        color: ${theme.secondaryBlack};

        &:hover {
          opacity: 0.6;
        }
      }
    `
  )}

  ${newDesignOnly(css`
    &.ant-alert {
      background-color: var(--surface-primary-action);
    }

    .ant-alert-message,
    .ant-alert-close-icon {
      color: var(--text-on-action);
    }

    .ant-alert-close-icon:hover {
      opacity: 0.6;
    }
  `)}
`;

// Exported for alert-call-site-parity.spec.
export const AnnouncementBannerAlert = StyledAlert;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

// Matches the message colour, so it has to branch with the background: on the lime accent fill the
// foreground is `text.onAction`, black in both modes. `--text-heading` would be white in dark.
const BannerLink = styled.a`
  font-weight: 600;
  text-decoration: underline;

  ${legacyOnly(
    theme => css`
      color: ${theme.secondaryBlack};
    `
  )}
  ${newDesignOnly(css`
    color: var(--text-on-action);
  `)}

  &:hover {
    opacity: 0.7;
  }
`;

export default AnnouncementBanner;
